import { db, functions } from './firebase'
import {
  collection, doc, addDoc, getDoc, query, orderBy, limit,
  onSnapshot, updateDoc, serverTimestamp, Timestamp
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { getSession } from './auth'
import { getPhrase } from '../vibes'

export interface Tingl {
  id?: string
  tinglId?: string
  from: string
  to: string
  fromName: string
  vibeKey: string
  vibeEmoji: string
  vibeLabel: string
  vibeColor: string
  phrase: string
  intensity?: number
  sentAt: Timestamp
  opened?: boolean
  openedAt?: Timestamp
}

// ─── Send a tingl — two taps, done ───────────────────────────────────────────
export async function sendTingl(
  toUid: string,
  vibeKey: string,
  vibeEmoji: string,
  vibeLabel: string,
  vibeColor: string,
  friendshipId: string,
  phraseOverride?: string,
  intensity: number = 1.0
): Promise<{ tinglId: string; phrase: string }> {
  const session = getSession()
  if (!session) throw new Error('Not signed in')

  const phrase = phraseOverride || getPhrase(vibeKey)
  const ref = await addDoc(collection(db, 'tingls'), {
    from: session.uid,
    to: toUid,
    fromName: session.username,
    vibeKey, vibeEmoji, vibeLabel, vibeColor,
    phrase, intensity,
    sentAt: serverTimestamp(),
    friendshipId,
  })
  return { tinglId: ref.id, phrase }
}

// ─── Mark opened ─────────────────────────────────────────────────────────────
export async function markOpened(tinglId: string) {
  const session = getSession()
  if (!session) return
  await updateDoc(doc(db, 'tingls', tinglId), {
    openedAt: serverTimestamp(),
  }).catch(() => {})
  await updateDoc(doc(db, 'users', session.uid, 'feed', tinglId), {
    opened: true,
  }).catch(() => {})
}

// ─── Fetch one tingl (receive screen via notification) ───────────────────────
export async function getTingl(tinglId: string): Promise<Tingl | null> {
  const snap = await getDoc(doc(db, 'tingls', tinglId))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Tingl) : null
}

// ─── Live feed of incoming tingls ────────────────────────────────────────────
export function listenToFeed(
  onUpdate: (tingls: Tingl[]) => void,
  onError?: (msg: string) => void
) {
  const session = getSession()
  if (!session) return () => {}
  const q = query(
    collection(db, 'users', session.uid, 'feed'),
    orderBy('sentAt', 'desc'),
    limit(50)
  )
  return onSnapshot(q, (snap) => {
    onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Tingl)))
  }, (err) => {
    console.error('feed listen', err)
    onError?.((err as any)?.code || err.message || 'feed listen denied')
  })
}

// ─── Share link (viral loop) ─────────────────────────────────────────────────
export async function createShareLink(
  vibeKey: string, vibeEmoji: string, vibeColor: string,
  phrase?: string, intensity?: number
): Promise<{ shortCode: string; url: string }> {
  const session = getSession()
  const fn = httpsCallable(functions, 'createShareLink')
  const res = await fn({
    vibeKey, vibeEmoji, vibeColor,
    phrase: phrase || null,
    intensity: intensity || 1.0,
    fromName: session?.username || null,
  })
  return res.data as { shortCode: string; url: string }
}

export async function openShareLink(shortCode: string) {
  const fn = httpsCallable(functions, 'openShareLink')
  const res = await fn({ shortCode })
  return res.data as any
}

// ─── MY TRANSMISSIONS — private log of feelings you've sent ─────────────────
export interface SentTingl {
  id?: string
  tinglId: string
  to: string
  toName: string
  vibeKey: string
  vibeEmoji: string
  vibeLabel: string
  vibeColor: string
  phrase: string
  intensity?: number
  sentAt: Timestamp
}

export function listenToSent(onUpdate: (tingls: SentTingl[]) => void) {
  const session = getSession()
  if (!session) return () => {}
  const q = query(
    collection(db, 'users', session.uid, 'sent'),
    orderBy('sentAt', 'desc'),
    limit(100)
  )
  return onSnapshot(q, (snap) => {
    onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() } as SentTingl)))
  }, (err) => console.error('sent listen', err))
}
